export function discountPercent(count) {
  if (count >= 4) {
    return 25;
  }
  if (count === 3) {
    return 20;
  }
  if (count === 2) {
    return 15;
  }
  return 0;
}

export function discountedPrice(price, percent) {
  return price - Math.round(price * percent / 100);
}

export function bundlePrices(products) {
  const prices = [];
  let original = 0;
  let total = 0;

  for (let index = 0; index < products.length; index++) {
    original += products[index].price;
    const target = discountedPrice(original, discountPercent(index + 1));
    const addition = target - total;

    if (addition < 0) {
      const remainingDiscount = -addition;
      const additionsTotal = prices.slice(1).reduce((sum, price) => sum + price, 0);
      const additionsDiscount = Math.min(remainingDiscount, additionsTotal);
      let cumulativePrice = 0;
      let allocatedDiscount = 0;

      if (additionsTotal > 0) {
        for (let previous = 1; previous < prices.length; previous++) {
          cumulativePrice += prices[previous];
          const cumulativeDiscount = Math.round(additionsDiscount * cumulativePrice / additionsTotal);
          prices[previous] -= cumulativeDiscount - allocatedDiscount;
          allocatedDiscount = cumulativeDiscount;
        }
      }

      prices[0] -= remainingDiscount - additionsDiscount;
    }

    prices.push(Math.max(0, addition));
    total = target;
  }

  return prices;
}

export function bundleTotal(products) {
  const original = products.reduce((total, product) => total + product.price, 0);
  return discountedPrice(original, discountPercent(products.length));
}

export function additionPrice(selected, candidate) {
  const currentTotal = bundleTotal(selected);
  const nextTotal = bundleTotal([...selected, candidate]);
  return Math.max(0, nextTotal - currentTotal);
}

function setHidden(element, hidden) {
  element.hidden = hidden;
  if (hidden) {
    element.style.display = 'none';
  } else {
    element.style.removeProperty('display');
  }
}

export function createBundleId(cryptoApi = crypto) {
  if (typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function bundleNodes(root, names) {
  const nodes = {};
  for (const name of names) {
    nodes[name] = root.querySelector(`[data-bundle-${name}]`);
  }
  return nodes;
}

let modalWasOpen = false;

export class E2BundleBuilder {
  constructor(dialog) {
    this.dialog = dialog;
    this.ui = bundleNodes(dialog, [
      'panel', 'overlay', 'form', 'close', 'error',
      'total', 'original-total', 'savings', 'discount', 'next',
      'progress', 'progress-line',
    ]);
    this.isOpen = false;
    this.dialog.inert = true;
    this.config = JSON.parse(dialog.querySelector('[data-bundle-config]').textContent);
    this.products = [...dialog.querySelectorAll('[data-bundle-product]')].map((element) => ({
      element,
      id: element.dataset.variantId,
      price: Number(element.dataset.price),
      available: element.dataset.available === 'true',
      base: element.hasAttribute('data-bundle-base'),
      selected: element.hasAttribute('data-bundle-base'),
      order: 0,
      nodes: bundleNodes(element, ['price', 'old-price', 'add', 'remove', 'base-status']),
    }));
    this.base = this.products.find((product) => product.base);
    this.button = this.ui.form.querySelector('[type="submit"]');
    this.ui['submit-label'] = this.button.querySelector('span');
    this.abort = new AbortController();
    this.busy = false;
    this.lastOrder = 0;

    this.listeners = [
      [dialog, 'click', (event) => this.handleClick(event)],
      [document, 'keydown', (event) => this.handleKeydown(event)],
      [this.ui.form, 'submit', (event) => {
        event.preventDefault();
        this.addToCart();
      }],
    ];
    for (const [target, type, listener] of this.listeners) {
      target.addEventListener(type, listener);
    }
    this.render();
  }

  handleClick(event) {
    const button = event.target.closest('button');
    if (event.target === this.ui.overlay || (button && button.hasAttribute('data-bundle-close'))) {
      this.close();
      return;
    }
    if (!button || this.busy) {
      return;
    }

    const row = button.closest('[data-bundle-product]');
    const product = this.products.find((item) => item.element === row);
    if (!product || product.base || !product.available) {
      return;
    }

    if (button.hasAttribute('data-bundle-add')) {
      this.add(product);
    }
    if (button.hasAttribute('data-bundle-remove')) {
      this.remove(product);
    }
    setHidden(this.ui.error, true);
    this.render();
  }

  handleKeydown(event) {
    if (!this.isOpen) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
    if (event.key !== 'Tab') {
      return;
    }

    const panel = this.ui.panel;
    const focusable = Array.from(panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.disabled && element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !panel.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
      event.preventDefault();
      const target = event.shiftKey ? last : first;
      (target || panel).focus();
    }
  }

  money(amount) {
    if (typeof window.formatMoney === 'function') {
      const template = document.createElement('template');
      template.innerHTML = window.formatMoney(amount, this.config.moneyFormat);
      return template.content.textContent;
    }
    if (!this.currencyFormatter) {
      this.currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.config.currency,
      });
    }
    return this.currencyFormatter.format(amount / 100);
  }

  selection() {
    return this.products.filter((product) => product.selected).sort((a, b) => a.order - b.order);
  }

  add(product) {
    if (product.base || !product.available || product.selected) {
      return;
    }
    product.order = ++this.lastOrder;
    product.selected = true;
  }

  remove(product) {
    if (product.base) {
      return;
    }
    product.selected = false;
  }

  render() {
    const selected = this.selection();
    const hasBundle = selected.length >= 2;
    const percent = discountPercent(selected.length);
    const original = selected.reduce((total, product) => total + product.price, 0);
    const prices = bundlePrices(selected);
    const total = discountedPrice(original, percent);
    const savings = original - total;

    for (const product of this.products) {
      const chosen = product.selected;
      const nodes = product.nodes;
      let price = chosen ? prices[selected.indexOf(product)] : product.price;
      if (!product.base && !chosen) {
        price = additionPrice(selected, product);
      }

      nodes.price.textContent = `${product.base ? '' : '+'}${this.money(price)}`;
      setHidden(nodes['old-price'], price === product.price);
      product.element.classList.toggle('is-selected', chosen);

      const status = nodes['base-status'];
      if (status) {
        setHidden(status, product.available);
      }

      if (nodes.add) {
        setHidden(nodes.add, chosen);
        nodes.add.disabled = this.busy || !product.available;
      }
      if (nodes.remove) {
        setHidden(nodes.remove, !chosen);
        if (chosen) {
          nodes.remove.style.display = 'block';
        }
        nodes.remove.disabled = this.busy;
      }
    }

    this.ui.total.textContent = this.money(total);
    const oldTotal = this.ui['original-total'];
    oldTotal.textContent = this.money(original);
    setHidden(oldTotal, savings === 0);
    this.ui.savings.textContent = this.config.messages.savings.replace('__amount__', this.money(savings));
    setHidden(this.ui.savings, savings === 0);

    this.ui.discount.textContent = this.config.messages.percentOff.replace('__percent__', percent);
    this.ui.next.textContent = percent === 25
      ? this.config.messages.maximumDiscount
      : this.config.messages.nextDiscount.replace('__percent__', discountPercent(selected.length + 1));
    const progressStep = Math.max(0, Math.min(selected.length - 1, 3));
    this.ui.progress.setAttribute('aria-valuenow', progressStep);
    this.ui['progress-line'].style.width = `${progressStep / 3 * 100}%`;

    this.button.disabled = this.busy || !hasBundle || !this.base || !this.base.available;
    this.ui['submit-label'].textContent = hasBundle
      ? this.config.messages.addToCart
      : this.config.messages.addProductToCart;
    this.button.classList.toggle('is-loading', this.busy);
    this.ui.close.disabled = this.busy;
    this.dialog.setAttribute('aria-busy', String(this.busy));
  }

  open(opener) {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: 'open-modal' });

    if (this.isOpen) {
      return;
    }
    this.opener = opener;
    this.isOpen = true;
    if (!document.querySelector('[data-e2-bundle-builder][data-bundle-open]')) {
      modalWasOpen = document.body.classList.contains('modal-open');
    }
    setHidden(this.dialog, false);
    // Apply the offscreen position before starting the opening transition.
    void this.dialog.offsetWidth;
    this.dialog.inert = false;
    this.dialog.setAttribute('data-bundle-open', '');
    document.body.classList.add('save-open', 'modal-open');
    this.ui.close.focus();
  }

  close() {
    if (!this.isOpen || this.busy) {
      return;
    }
    this.isOpen = false;
    // Keep the drawer rendered until CSS finishes its closing transition.
    this.dialog.removeAttribute('data-bundle-open');
    const anotherOpen = Boolean(document.querySelector('[data-e2-bundle-builder][data-bundle-open]'));
    document.body.classList.toggle('save-open', anotherOpen);
    if (!anotherOpen && !modalWasOpen) {
      document.body.classList.remove('modal-open');
    }
    if (this.opener) {
      this.opener.focus();
    }
    this.dialog.inert = true;
  }

  async withBusy(action) {
    if (this.busy || this.destroyed) {
      return;
    }
    this.busy = true;
    setHidden(this.ui.error, true);
    this.render();
    try {
      return await action();
    } catch (error) {
      if (!this.destroyed && error.name !== 'AbortError') {
        const message = error.message || this.config.messages.error;
        const cart = document.querySelector('mini-cart');
        if (cart && typeof cart.handleErrors === 'function') {
          cart.handleErrors(message);
        } else {
          this.ui.error.textContent = message;
          setHidden(this.ui.error, false);
        }
      }
    } finally {
      this.busy = false;
      if (!this.destroyed) {
        this.render();
      }
    }
  }

  async request(path, body) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const options = {
      method: body ? 'POST' : 'GET',
      headers: { 'Accept': 'application/json' },
      signal: this.abort.signal,
    };
    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const response = await fetch(`${root}${path}`, options);
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(typeof result.description === 'string' ? result.description : this.config.messages.error);
    }
    return result;
  }

  async resetBundle() {
    const cart = await this.request('cart.js');
    const updates = {};

    for (const item of cart.items) {
      if (item.properties && item.properties._e2_bundle_builder_id === this.bundleId) {
        updates[item.key] = 0;
      }
    }

    if (Object.keys(updates).length) {
      await this.request('cart/update.js', { updates });
    }
  }

  async addBundleProducts(selected) {
    const baseImage = this.base.element && this.base.element.querySelector('img');
    const baseImageUrl = baseImage ? baseImage.src.split(/[?#]/)[0] : '';
    const items = [];

    selected.forEach((product, position) => {
      const existing = items.find((item) => item.id === Number(product.id));

      if (existing) {
        existing.quantity += 1;
        existing.properties._e2_bundle_builder_quantity = String(existing.quantity);
        return;
      }

      items.push({
        id: Number(product.id),
        quantity: 1,
        properties: {
          _e2_bundle_builder_id: this.bundleId,
          _e2_bundle_builder_base: this.base.id,
          _e2_bundle_builder_position: String(position),
          _e2_bundle_builder_native: '1',
          ...(product.base && baseImageUrl ? { _e2_bundle_builder_image: baseImageUrl } : {}),
        },
      });
    });

    await this.request('cart/add.js', { items });
  }

  async addToCart() {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: 'custom-add-to-cart' });

    if (!this.base || !this.base.available || this.selection().length < 2) {
      return;
    }
    const cart = document.querySelector('mini-cart');
    const added = await this.withBusy(async () => {
      if (this.bundleId) {
        await this.resetBundle();
      } else {
        this.bundleId = createBundleId();
      }

      if (this.destroyed) {
        return;
      }
      await this.addBundleProducts(this.selection());

      if (this.destroyed) {
        return;
      }
      await cart.load();
      return true;
    });
    if (!added || this.destroyed) {
      return;
    }
    this.bundleId = null;
    this.close();
    for (const product of this.products) {
      product.selected = product === this.base;
      product.order = 0;
    }
    this.lastOrder = 0;
    this.ui.error.textContent = '';
    this.render();
    cart.open();
  }

  destroy() {
    this.destroyed = true;
    this.busy = false;
    this.close();
    for (const [target, type, listener] of this.listeners) {
      target.removeEventListener(type, listener);
    }
    this.abort.abort();
  }
}

if (typeof document !== 'undefined') {
  const instances = new WeakMap();

  function builders(root) {
    const dialogs = [...root.querySelectorAll('[data-e2-bundle-builder]')];
    if (root.matches && root.matches('[data-e2-bundle-builder]')) {
      dialogs.unshift(root);
    }
    return dialogs;
  }

  function initialize(root) {
    for (const dialog of builders(root)) {
      if (!instances.has(dialog)) {
        instances.set(dialog, new E2BundleBuilder(dialog));
      }
    }
    if (new URLSearchParams(window.location.search).has('bundle-builder')) {
      for (const opener of root.querySelectorAll('.product-info__save[data-e2-bundle-builder-open]')) {
        setHidden(opener, false);
      }
    }
  }

  initialize(document);

  function openBuilder(event) {
    const opener = event.target.closest('[data-e2-bundle-builder-open]');
    if (!opener) {
      return;
    }
    const dialog = document.getElementById(opener.dataset.e2BundleBuilderOpen);
    const builder = dialog && instances.get(dialog);
    if (builder) {
      event.preventDefault();
      builder.open(opener);
    }
  }

  document.addEventListener('click', openBuilder);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event.target.closest('[data-e2-bundle-builder-open][role="button"]')) {
      openBuilder(event);
    }
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const dialog of builders(event.target)) {
      const builder = instances.get(dialog);
      if (builder) {
        builder.destroy();
      }
      instances.delete(dialog);
    }
  });
}
